import React, {ReactElement} from "react";
import {Box, Button, darken, Typography} from "@mui/material";
import {StyledBoxColumn} from "../../../shared/StyledComponents";
import {DW_SM, PD_LG, PD_MD, PD_SM} from "../../../shared/dimens";
import {
  createBoardTiles,
  DubbleupDifficultyLevel,
  DubbleupGameBoard,
  DubbleupGameConfig,
  DubbleupGameData,
  TileData
} from "./types";
import {BaseApp, DIALOG_FLAG_SHOW_CLOSE} from "../../../shared/BaseApp";
import {GameFragment, GameFragmentProps, GameFragmentState} from "../GameFragment";
import {HelpOutlineOutlined, MoreHorizOutlined} from "@mui/icons-material";

import {GAME_DUBBLEUP} from "../../Games";
import {Game} from "../../types";
import {StringUtil} from "../../../shared/string_util";
import {$KTS} from "../../../shared/keytext";
import {GameOverResult, GameOverResultType} from "../types";
import {colorDarkBrown, colorEarthBrown, colorEarthLightBrown} from "../../../shared/colors";
import {md5_uuid} from "../../../shared/md5";
import {Throttler} from "../../../shared/file_util";
import {Action} from "../../../shared/types";

export type DubbleupGameFragmentProps = GameFragmentProps & {}

enum DubbleupGameStatus {
  STATUS_IN_PROGRESS,
  STATUS_COMPLETE, // Finished, and solved
}

type DubbleupGameFragmentState = GameFragmentState<DubbleupGameConfig, DubbleupGameData> & {
  gameStatus: DubbleupGameStatus,
  moves: number,
}

const TILE_COLORS = {
  "2": "#f7f0d8",
  "4": "#ede0c8",
  "8": "#f2b179",
  "16": "#f59563",
  "32": "#f67c5f",
  "64": "#f65e3b",
  "128": darken("#edcf72", 0.1),
  "256": darken("#edcc61", 0.1),
  "512": darken("#edc850", 0.1),
  "1024": darken("#edc53f", 0.1),
  "2048": darken("#edc22e", 0.1),
  "4096": darken("#f2b179", 0.2),
  "8192": darken("#f59563", 0.2),
  "16384": darken("#f67c5f", 0.2),
}

const ANIM_DURATION_MS = 300;

function Tile(props: {
  rowIndex: number,
  colIndex: number,
  tileSize: number,
  tile: number,
}): ReactElement {
  const tile = props.tile.toString();
  return <Box
    style={{
      position: "absolute",
      transition: `all ${ANIM_DURATION_MS}ms ease-in-out`,
      left: props.colIndex * props.tileSize,
      top: props.rowIndex * props.tileSize,
      width: props.tileSize,
      height: props.tileSize,
      padding: PD_SM,
    }}>
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      height: "100%",
      borderRadius: props.tileSize,
      background: props.tile ? TILE_COLORS[tile] : darken(colorEarthLightBrown, 0.1)
    }}>
      {props.tile !== 0
        ? <Typography style={{fontSize: 72 - Math.log2(props.tile) * 3, fontWeight: "bold", color: colorDarkBrown}}>
          {tile}
        </Typography>
        : null}
    </div>
  </Box>;
}

export class DubbleupupGameFragment extends GameFragment<DubbleupGameFragmentProps, DubbleupGameConfig, DubbleupGameData, DubbleupGameFragmentState> {

  private board: DubbleupGameBoard;

  private getTiles(): TileData[][] {
    return this.board.tiles;
  }

  private readonly keyListener = (ev: KeyboardEvent) => {
    const complete = this.state.gameStatus === DubbleupGameStatus.STATUS_COMPLETE;
    if (complete) {
      return;
    }
    switch (ev.code) {
      default:
        return;
      case "ArrowUp":
        this.moveTiles("Up");
        break;
      case "ArrowDown":
        this.moveTiles("Down");
        break;
      case "ArrowLeft":
        this.moveTiles("Left");
        break;
      case "ArrowRight":
        this.moveTiles("Right");
        break;
    }
    ev.preventDefault();
  };

  private touchStartX: number;
  private touchStartY: number;

  private readonly touchStartListener = (ev: TouchEvent) => {
    const complete = this.state.gameStatus === DubbleupGameStatus.STATUS_COMPLETE;
    if (complete) {
      return;
    }
    this.touchStartX = ev.changedTouches[0].clientX;
    this.touchStartY = ev.changedTouches[0].clientY;
  };

  private readonly touchEndListener = (ev: TouchEvent) => {
    const complete = this.state.gameStatus === DubbleupGameStatus.STATUS_COMPLETE;
    if (complete) {
      return;
    }
    const dx = ev.changedTouches[0].clientX - this.touchStartX;
    const dy = ev.changedTouches[0].clientY - this.touchStartY;
    this.touchStartX = this.touchStartY = undefined;
    if (Math.sqrt(dx * dx + dy * dy) < 8) {
      return;
    }
    if (Math.abs(dx) < Math.abs(dy)) {
      if (dy < 0) {
        this.moveTiles("Up");
      } else {
        this.moveTiles("Down");
      }
    } else {
      if (dx < 0) {
        this.moveTiles("Left");
      } else {
        this.moveTiles("Right");
      }
    }
  };

  private swapTiles(i1: number, j1: number, i2: number, j2: number) {
    const tiles = this.getTiles();
    const tmp = tiles[i1][j1];
    tiles[i1][j1] = tiles[i2][j2];
    tiles[i2][j2] = tmp;
  }

  private readonly throttler = new Throttler(ANIM_DURATION_MS * 1.5);

  private moveTiles(action: "Up" | "Down" | "Left" | "Right") {
    this.throttler.run(() => this.moveTilesInternal(action));
  }

  private moveTilesInternal(action: "Up" | "Down" | "Left" | "Right") {
    const tiles = this.getTiles();
    const tileCount = tiles.length;
    let offset: number;
    switch (action) {
      case "Up":
        for (let j = 0; j < tileCount; j++) {
          offset = 0;
          for (let i = 0; i < tileCount; i++) {
            if (tiles[i][j].value === 0) {
              offset++;
            } else if (offset > 0) {
              this.swapTiles(i - offset, j, i, j);
            }
          }
        }
        for (let j = 0; j < tileCount; j++) {
          for (let i = 0; i < tileCount - 1; i++) {
            if (tiles[i][j].value === tiles[i + 1][j].value) {
              this.swapTiles(i, j, i + 1, j);
              tiles[i][j].value *= 2;
              tiles[i + 1][j].value = 0;
              for (let k = i + 1; k < tileCount - 1; k++) {
                this.swapTiles(k, j, k + 1, j);
              }
            }
          }
        }
        break;
      case "Left":
        for (let i = 0; i < tileCount; i++) {
          offset = 0;
          for (let j = 0; j < tileCount; j++) {
            if (tiles[i][j].value === 0) {
              offset++;
            } else if (offset > 0) {
              this.swapTiles(i, j - offset, i, j);
            }
          }
        }
        for (let i = 0; i < tileCount; i++) {
          for (let j = 0; j < tileCount - 1; j++) {
            if (tiles[i][j].value === tiles[i][j + 1].value) {
              this.swapTiles(i, j, i, j + 1);
              tiles[i][j].value *= 2;
              tiles[i][j + 1].value = 0;
              for (let k = j + 1; k < tileCount - 1; k++) {
                this.swapTiles(i, k, i, k + 1);
              }
            }
          }
        }
        break;
      case "Down":
        for (let j = 0; j < tileCount; j++) {
          offset = 0;
          for (let i = tileCount - 1; i >= 0; i--) {
            if (tiles[i][j].value === 0) {
              offset++;
            } else if (offset > 0) {
              this.swapTiles(i + offset, j, i, j);
            }
          }
        }
        for (let j = 0; j < tileCount; j++) {
          for (let i = tileCount - 1; i > 0; i--) {
            if (tiles[i][j].value === tiles[i - 1][j].value) {
              this.swapTiles(i, j, i - 1, j);
              tiles[i][j].value *= 2;
              tiles[i - 1][j].value = 0;
              for (let k = i - 1; k > 0; k--) {
                this.swapTiles(k, j, k - 1, j);
              }
            }
          }
        }
        break;
      case "Right":
        for (let i = 0; i < tileCount; i++) {
          offset = 0;
          for (let j = tileCount - 1; j >= 0; j--) {
            if (tiles[i][j].value === 0) {
              offset++;
            } else if (offset > 0) {
              this.swapTiles(i, j + offset, i, j);
            }
          }
        }
        for (let i = 0; i < tileCount; i++) {
          for (let j = tileCount - 1; j > 0; j--) {
            if (tiles[i][j].value === tiles[i][j - 1].value) {
              this.swapTiles(i, j, i, j - 1);
              tiles[i][j].value *= 2;
              tiles[i][j - 1].value = 0;
              for (let k = j - 1; k > 0; k--) {
                this.swapTiles(i, k, i, k - 1);
              }
            }
          }
        }
        break;
    }
    const emptyIndices: number[] = [];
    for (let i = 0; i < tileCount; i++) {
      for (let j = 0; j < tileCount; j++) {
        if (tiles[i][j].value === 0) {
          emptyIndices.push(i * tileCount + j);
        }
      }
    }
    if (emptyIndices.length <= 0) {
      this.props.gameFragmentListener?.onStatusChanged("Game complete.");
      this.finishGame(new GameOverResult(GAME_DUBBLEUP.id, GameOverResultType.LOST, this.board.level.name, 0, 0, "You lost this game.", false));
      this.setState({
        gameStatus: DubbleupGameStatus.STATUS_COMPLETE,
      });
    } else {
      setTimeout(() => {
        const genIndex = emptyIndices.splice(Math.floor(Math.random() * emptyIndices.length), 1)[0];
        tiles[Math.floor(genIndex / tileCount)][genIndex % tileCount] = {id: md5_uuid(), value: 2};
        this.setState({
          moves: this.state.moves + 1,
        });
      }, ANIM_DURATION_MS * 1.5);
    }
    this.forceUpdate();
    // this.printBoard();
  }

  private printBoard() {
    let s = "";
    const tiles = this.getTiles();
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        s += " " + tiles[i][j].value;
      }
      s += "\n";
    }
    console.log(s);
  }

  protected getGame(): Game {
    return GAME_DUBBLEUP;
  }

  protected onCreateState(): DubbleupGameFragmentState {
    return {
      ...super.onCreateState(),
      gameStatus: DubbleupGameStatus.STATUS_IN_PROGRESS,
      moves: 0,
    };
  }

  componentDidMount() {
    super.componentDidMount();
    window.addEventListener("keydown", this.keyListener);
    window.addEventListener("touchstart", this.touchStartListener);
    window.addEventListener("touchend", this.touchEndListener);
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    window.removeEventListener("keydown", this.keyListener);
    window.removeEventListener("touchstart", this.touchStartListener);
    window.removeEventListener("touchend", this.touchEndListener);
  }

  componentDidUpdate(prevProps: Readonly<DubbleupGameFragmentProps>, prevState: Readonly<DubbleupGameFragmentState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (prevState.gameData !== this.state.gameData) {
      this.board = this.state.gameData.gamePlay.jsonData as DubbleupGameBoard;
    }
    const moves = this.state.moves;
    if (prevState.moves !== moves) {
      const board = this.board;
      const tilesCount = 4;
      const totalTiles = tilesCount * tilesCount;
      let complete: boolean = false;
      for (let i = 0; i < totalTiles; i++) {
        if (board.tiles[Math.floor(i / 4)][i % 4].value >= this.board.level.maxTileValue) {
          complete = true;
          break;
        }
      }
      if (complete) {
        this.props.gameFragmentListener?.onStatusChanged("Game complete.");
        let resultText: string = "You completed %{level} game in %{moves} moves.";
        switch (this.state.gameConfig.level) {
          case DubbleupDifficultyLevel.EASY:
            resultText = StringUtil.format(resultText, $KTS("level", "an easy"));
            break;
          case DubbleupDifficultyLevel.MEDIUM:
            resultText = StringUtil.format(resultText, $KTS("level", "a medium"));
            break;
          case DubbleupDifficultyLevel.HARD:
            resultText = StringUtil.format(resultText, $KTS("level", "a hard"));
            break;
        }
        resultText = StringUtil.format(resultText, $KTS("moves", moves.toString()));
        this.finishGame(new GameOverResult(GAME_DUBBLEUP.id, GameOverResultType.WON, this.board.level.name, this.board.getPoints(moves), 0, resultText, false));
        this.setState({
          gameStatus: DubbleupGameStatus.STATUS_COMPLETE,
        });
      }
    }
  }

  protected async fetchOnMount(forceReload?: boolean): Promise<void> {
    const gameConfig = this.state.gameConfig;
    if (!gameConfig) {
      return;
    }
    this.setState({
      gameData: await DubbleupGameData.create(gameConfig),
    });
  }

  protected renderConfigContent(): React.ReactElement {
    return this.renderConfigContentForGame(GAME_DUBBLEUP, <>
      {DubbleupDifficultyLevel.VALUES.map(level =>
        <Button variant="contained"
                onClick={() => this.setState({gameConfig: new DubbleupGameConfig(level)})}>{level.text}</Button>)}
    </>);
  }

  protected renderToolbarTitle(): React.ReactElement {
    const level = (this.state.gameData.gamePlay.jsonData as DubbleupGameBoard).level;
    return <Typography
      style={{marginLeft: PD_MD,}}>{level.text} Goal: {level.maxTileValue}</Typography>
  }

  protected renderToolbarButtons() {
    return <>
      <Button onClick={() => BaseApp.CONTEXT.showDialog({flags: DIALOG_FLAG_SHOW_CLOSE}, props => {
        return <StyledBoxColumn style={{padding: PD_LG, maxWidth: DW_SM, alignItems: "center", gap: PD_LG}}>
          <Typography variant="h5">How to play Dubble Up</Typography>
          <Typography>Use your arrow keys or swipe to move tiles on a 4x4 grid; when two tiles with the same number
            touch, they merge into one tile with a new sum. Your aim is to reach the goal tile by merging tiles until
            their values double to that number. After each move, a new tile with a value of 2 appears in a random empty
            space.
          </Typography>
        </StyledBoxColumn>;
      })}>
        <HelpOutlineOutlined/>
      </Button>
      <Button onClick={(event) => BaseApp.CONTEXT.showActions(event.target as HTMLButtonElement, null, [
        new Action("Restart", () => {
          const tiles = createBoardTiles();
          for (let i = 0; i < this.board.tiles.length; i++) {
            for (let j = 0; j < this.board.tiles[i].length; j++) {
              this.board.tiles[i][j] = tiles[i][j];
            }
          }
          this.setState({moves: 0});
        }),
      ])}>
        <MoreHorizOutlined/>
      </Button>
    </>
  }

  protected renderGameContent(): ReactElement | null {
    const board = this.board;
    if (!board) {
      return null;
    }
    const tilesCount = board.tiles.length;
    const tileSize = 640 / tilesCount;
    const zoom = Math.min(1, (window.innerWidth - PD_LG * 2) / (tileSize * tilesCount));
    return <StyledBoxColumn style={{
      flexGrow: 1,
      alignItems: "center",
      padding: PD_LG,
      userSelect: "none",
    }}>
      <Box style={{
        position: "relative",
        width: 640,
        height: 640,
        zoom: zoom,
        outline: colorEarthBrown + " 8px solid",
        borderRadius: PD_LG,
        background: colorEarthBrown,
      }}>
        {board.tiles.map((row, rowIndex) => row.map((_, colIndex) => {
          return <Tile
            key={`base-${rowIndex}-${colIndex}`}
            rowIndex={rowIndex}
            colIndex={colIndex}
            tileSize={tileSize}
            tile={0}/>;
        })).flat()}
        {board.tiles.map((row, rowIndex) => row.map((tile, colIndex) => {
          if (tile.value === 0) {
            return null;
          }
          return <Tile
            key={tile.id}
            rowIndex={rowIndex}
            colIndex={colIndex}
            tileSize={tileSize}
            tile={tile.value}/>;
        })).flat()}
      </Box>
    </StyledBoxColumn>;
  }
}